import type { TrainingAnswers, TrainingPlayerTextGroup } from '@hcc/types';
import { ShortTextAnswer } from './answers/ShortTextAnswer';

/**
 * A "Questions and answers" page: several free-text questions, each with
 * its own box (or a date / time picker). Rows are independent questions, so
 * answers are keyed by row id, exactly like a question grid.
 *
 * Two layouts: stacked (box under the prompt, the default) and inline
 * (prompt and box side by side on wider screens, collapsing to stacked on a
 * phone), the compact form for a page of short personal details.
 */
export function TextGroupSlide({
  group,
  answers,
  onChange,
  disabled,
}: {
  group: TrainingPlayerTextGroup;
  answers: TrainingAnswers;
  onChange: (rowId: string, text: string) => void;
  disabled?: boolean;
}) {
  const inline = group.layout === 'inline';
  return (
    <div className="space-y-5">
      {group.title ? <h2 className="text-lg font-semibold text-fg">{group.title}</h2> : null}
      {group.intro ? <p className="text-sm text-fg-muted">{group.intro}</p> : null}
      <ol className={inline ? 'divide-y divide-divider/10' : 'space-y-5'}>
        {group.rows.map((row) => {
          const labelId = `tg-${row.id}`;
          const value = typeof answers[row.id] === 'string' ? (answers[row.id] as string) : '';
          const kind = row.input ?? 'text';
          const label = (
            <p id={labelId} className="text-sm font-medium text-fg">
              {row.prompt}
              {row.optional ? (
                <span className="ml-1.5 text-xs font-normal text-fg-dim">(optional)</span>
              ) : null}
            </p>
          );
          const box = (
            <ShortTextAnswer
              value={value}
              onChange={(next) => onChange(row.id, next)}
              disabled={disabled}
              ariaLabelledBy={labelId}
              multiline={kind === 'text' && row.multiline}
              placeholder={row.placeholder}
              kind={kind}
            />
          );
          return inline ? (
            <li
              key={row.id}
              className="grid gap-2 py-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:items-center sm:gap-4"
            >
              {label}
              {box}
            </li>
          ) : (
            <li key={row.id} className="space-y-2">
              {label}
              {box}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
